import { driver, type Alignment, type DriveStep, type Side } from 'driver.js';
import 'driver.js/dist/driver.css';
import './welcome-tour.css';

/**
 * Yonlendirme Faz 4.2 — tur motoru. Karsilama (yolculuk) turu ve sayfa turlari
 * ayni driver.js ayarlarini, temali pencereyi ve hedef bulma kurallarini
 * buradan kullanir. Hedefler `data-tour="..."` ozniteligiyle bulunur (sinif
 * adlarina baglanmaz).
 *
 * Adimlar farkli sayfalarda olabilir (`path`): motor siradaki adimin sayfasina
 * gider, hedef cizilip gorunur olana kadar bekler, sonra pencereyi acar.
 * Hedefi hic gorunmeyen adim atlanir; bir bolumun (sayfanin) ilk adimi
 * gorunmezse o sayfanin tum adimlari atlanir.
 */

export type TourStepDefinition = {
    titleKey: string;
    descKey: string;
    /** `data-tour` degerleri; sayfada bulunan ilk hedef kullanilir. Bos ise pencere ortada acilir. */
    targets?: string[];
    side?: Side;
    align?: Alignment;
};

export type TourStep = TourStepDefinition & {
    /** Adimin gosterildigi sayfa (orn. `/tables`); bos ise bulunulan sayfa. */
    path?: string;
    /** Pencerede sayacin onunde gorunen sayfa adi (ceviri anahtari). */
    labelKey?: string;
    /** Ayni sayfanin adimlari ayni bolumdedir; bolumun ilk adimi gorunmezse bolum atlanir. */
    sectionId?: string;
    /** Hedefin gorunmesi icin beklenecek en uzun sure (ms); verilmezse beklenmez. */
    waitMs?: number;
};

export type ElementVisibility = (element: Element) => boolean;

type Translate = (key: string) => string;

/** Turun sayfalar arasi gezinmesi; `navigate`, yeni sayfa cizildiginde cozulur. */
export type TourNavigation = {
    currentPath: () => string;
    navigate: (path: string) => Promise<void>;
};

export type TourCloseSummary = {
    /** Kullaniciya gosterilen adimlarin sira numaralari. */
    shownIndexes: ReadonlySet<number>;
};

export type TourHandle = {
    /** Turu kapatir (`onClose` bir kez cagrilir). */
    destroy: () => void;
    /** Tur su an siradaki adimi hazirliyor mu (sayfa degistirme / hedef bekleme)? */
    isBusy: () => boolean;
};

const TARGET_POLL_MS = 100;
/** Sayfa gecisi bu sure icinde tamamlanmazsa yine de hedef aranir. */
export const TOUR_NAVIGATION_TIMEOUT_MS = 3000;
/** Sayfa degisirken pencere ve karartma bu sinifla gizlenir (welcome-tour.css). */
export const TOUR_NAVIGATING_CLASS = 'wc-tour-navigating';

/** Ekranda yer kaplayan ve ekranin yatay sinirlari icinde kalan oge gorunur sayilir. */
export const isElementShown: ElementVisibility = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth;
};

export const tourTargetSelector = (target: string) => `[data-tour="${target}"]`;

export const findTourTarget = (
    targets: string[],
    accept: ElementVisibility = isElementShown,
    root: ParentNode = document,
): Element | null => {
    for (const target of targets) {
        const element = root.querySelector(tourTargetSelector(target));
        if (element && accept(element)) return element;
    }
    return null;
};

/** Hedefsiz adimi tutar; hedefi sayfada olmayan ya da gorunmeyen adimi atlar. */
export const resolveTourSteps = <T extends TourStepDefinition>(
    definitions: T[],
    root: ParentNode = document,
    isVisible: ElementVisibility = isElementShown,
): T[] =>
    definitions.filter((definition) => !definition.targets?.length || findTourTarget(definition.targets, isVisible, root));

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** Kosul saglanana ya da sure dolana kadar bekler; sonucta kosulun son durumunu doner. */
export const waitUntil = async (condition: () => boolean, timeoutMs: number, pollMs = TARGET_POLL_MS) => {
    const deadline = Date.now() + timeoutMs;
    while (!condition()) {
        if (Date.now() >= deadline) return false;
        await wait(pollMs);
    }
    return true;
};

/** Hedeflerden biri gorunene ya da sure dolana kadar bekler; gorunmezse `null`. */
export const waitForTourTarget = async (targets: string[], timeoutMs: number) => {
    await waitUntil(() => findTourTarget(targets) !== null, timeoutMs);
    return findTourTarget(targets);
};

const normalizePath = (path: string) => `/${path.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '')}`;

export const isSamePath = (a: string, b: string) => normalizePath(a) === normalizePath(b);

const addSkipButton = (footerButtons: HTMLElement, label: string, onSkip: () => void) => {
    const skipButton = document.createElement('button');
    skipButton.type = 'button';
    skipButton.className = 'wc-tour-skip';
    skipButton.textContent = label;
    skipButton.addEventListener('click', onSkip);
    footerButtons.prepend(skipButton);
};

const setNavigating = (navigating: boolean) => document.body.classList.toggle(TOUR_NAVIGATING_CLASS, navigating);

type RunTourOptions = {
    steps: TourStep[];
    t: Translate;
    /** Tur bitirildiginde, atlandiginda ya da kapatildiginda bir kez cagrilir. */
    onClose: (summary: TourCloseSummary) => void;
    /** Adimlar baska sayfalardaysa gezinme; verilmezse tur bulunulan sayfada kalir. */
    navigation?: TourNavigation;
};

/**
 * Turu baslatir. Ileri: siradaki adim hazirlanir (gerekirse sayfasina gidilir,
 * hedef beklenir); hazirlanamayan adim atlanir. Geri: daha once gosterilen
 * bir onceki adima (gerekirse onceki sayfaya) donulur. Sayac planlanan tum
 * adimlar uzerinden "{current}/{total}" gosterir; atlanan adimlarda ileri sicrar.
 */
export const runTour = ({ steps, t, onClose, navigation }: RunTourOptions): TourHandle => {
    const elements = new Map<number, Element>();
    const shownIndexes = new Set<number>();
    let currentIndex = -1;
    let busy = false;
    let closed = false;

    const close = () => {
        if (closed) return;
        closed = true;
        setNavigating(false);
        onClose({ shownIndexes });
    };

    const progressTemplate = t('tour.progress').replace('{current}', '{{current}}').replace('{total}', '{{total}}');
    const driveSteps: DriveStep[] = steps.map((step, index) => ({
        ...(step.targets?.length ? { element: () => elements.get(index) as Element } : {}),
        popover: {
            title: t(step.titleKey),
            description: t(step.descKey),
            progressText: step.labelKey ? `${t(step.labelKey)} · ${progressTemplate}` : progressTemplate,
            ...(step.targets?.length ? { side: step.side, align: step.align } : {}),
        },
    }));

    const isSectionStart = (index: number) =>
        Boolean(steps[index].sectionId) && steps[index - 1]?.sectionId !== steps[index].sectionId;

    const sectionEnd = (index: number) => {
        let end = index;
        while (steps[end + 1]?.sectionId === steps[index].sectionId) end += 1;
        return end;
    };

    /** Adimi gosterime hazirlar: sayfasina gider, hedefini bekler. Hedef gorunmezse `false`. */
    const prepare = async (index: number) => {
        const step = steps[index];
        if (step.path && navigation && !isSamePath(navigation.currentPath(), step.path)) {
            setNavigating(true);
            await Promise.race([navigation.navigate(step.path), wait(TOUR_NAVIGATION_TIMEOUT_MS)]);
        }
        if (!step.targets?.length) return true;

        const element = await waitForTourTarget(step.targets, step.waitMs ?? 0);
        if (!element) return false;
        element.scrollIntoView?.({ block: 'nearest' });
        elements.set(index, element);
        return true;
    };

    const findNext = async (from: number) => {
        for (let index = from; index < steps.length && !closed; ) {
            if (await prepare(index)) return index;
            index = isSectionStart(index) ? sectionEnd(index) + 1 : index + 1;
        }
        return null;
    };

    /** Daha once gosterilen onceki adimlar; hicbiri hazirlanamazsa bulunulan adim. */
    const findPrevious = async () => {
        const candidates = [...shownIndexes].filter((index) => index < currentIndex).sort((a, b) => b - a);
        for (const index of [...candidates, currentIndex]) {
            if (closed) return null;
            if (await prepare(index)) return index;
        }
        return null;
    };

    const tour = driver({
        steps: driveSteps,
        popoverClass: 'wc-tour-popover',
        showProgress: true,
        nextBtnText: t('tour.next'),
        prevBtnText: t('tour.prev'),
        doneBtnText: t('tour.done'),
        showButtons: ['next', 'previous'],
        allowClose: true,
        overlayClickBehavior: 'none',
        disableActiveInteraction: true,
        stagePadding: 6,
        stageRadius: 14,
        onNextClick: () => {
            void move(1);
        },
        onPrevClick: () => {
            void move(-1);
        },
        onPopoverRender: (popover, { driver: activeTour }) => {
            if (!activeTour.isLastStep()) addSkipButton(popover.footerButtons, t('tour.skip'), () => activeTour.destroy());
        },
        onDestroyed: close,
    });

    const show = (index: number) => {
        currentIndex = index;
        shownIndexes.add(index);
        if (tour.isActive()) tour.moveTo(index);
        else tour.drive(index);
    };

    const destroy = () => {
        if (tour.isActive()) tour.destroy();
        else close();
    };

    async function move(direction: 1 | -1) {
        if (busy || closed) return;
        busy = true;
        try {
            const index = direction === 1 ? await findNext(currentIndex + 1) : await findPrevious();
            if (closed) return;
            if (index !== null) show(index);
            else if (direction === 1) destroy();
        } finally {
            busy = false;
            setNavigating(false);
        }
    }

    void move(1);
    return { destroy, isBusy: () => busy };
};
